// src/features/invoice/fakturaGrunnlag.js
// Delelinjer og totaler for servicefakturaen (ServiceInvoiceModal). Felles for
// WorkshopDetails og /skoyteslip, så begge viser samme beløp.

// Normaliserer delelinjer (eldre poster har delen i pl.part, pl.partSnapshot osv.).
export function tilDelelinjer(partsLines) {
  if (!Array.isArray(partsLines)) return [];

  return partsLines.map((pl) => {
    const src =
      pl.part ||
      pl.partId ||
      pl.partRef ||
      pl.catalogPart ||
      pl.partSnapshot ||
      {};

    return {
      ...pl,
      product: pl.product ?? src.product ?? src.name ?? "Del",
      brand: pl.brand ?? src.brand ?? "",
      ean: pl.ean ?? src.ean ?? "",
      price: Number(pl.price ?? src.price ?? src.unitPrice ?? 0),
      qty: Number(pl.qty ?? 1),
      discount: Number(pl.discount ?? 0),
      discountPercent: Number(pl.discountPercent ?? 0),
    };
  });
}

export function beregnTotaler(serviceLines = [], partLines = [], fraktLines = []) {
  const arbeid = serviceLines.reduce((sum, l) => {
    if (l.isWarranty || l.reklamasjon || l.gratisarbeid) return sum;
    return sum + (Number(l.pris) || 0);
  }, 0);

  const deler = partLines.reduce((sum, l) => {
    if (l.isWarranty) return sum;

    const price = Number(l.price) || 0;
    const qty = Math.max(1, Number(l.qty) || 1);
    const subtotal = price * qty;

    let discountKr = Math.max(0, Number(l.discount) || 0);
    const discountPercent = Number(l.discountPercent) || 0;

    if (!discountKr && discountPercent && subtotal > 0) {
      discountKr = Math.round((subtotal * discountPercent) / 100);
    }

    const line = Math.max(0, subtotal - discountKr);
    return sum + line;
  }, 0);

  const frakt = fraktLines.reduce((sum, f) => sum + (Number(f.pris) || 0), 0);

  return {
    arbeid,
    deler,
    frakt,
    total: arbeid + deler + frakt,
    min: serviceLines.reduce((s, l) => s + (Number(l.tidsbruk) || 0), 0),
  };
}

// Dekningsbidrag på deler, regnet som i nettbutikkens RedigerProduktModal:
// salgsprisen er inkl. 25 % mva, kostprisen eks. mva. Bidrag = salg eks. mva
// minus kostpris; dekningsgrad = bidrag / salg eks. mva. Bare for de ansatte.
const MVA = 1.25;

export function linjesum(l) {
  const subtotal = (Number(l.price) || 0) * Math.max(1, Number(l.qty) || 1);
  let discountKr = Math.max(0, Number(l.discount) || 0);
  const discountPercent = Number(l.discountPercent) || 0;
  if (!discountKr && discountPercent && subtotal > 0) {
    discountKr = Math.round((subtotal * discountPercent) / 100);
  }
  return Math.max(0, subtotal - discountKr);
}

// null når kostpris mangler (eldre linjer, eller del uten kostpris i katalogen)
// eller linjen er garanti/reklamasjon (kunden betaler ikke for delen).
export function delelinjeBidrag(l) {
  if (l.isWarranty || l.kostpris == null || l.kostpris === "") return null;
  const kostpris = Number(l.kostpris);
  if (!Number.isFinite(kostpris)) return null;
  const salgEksMva = linjesum(l) / MVA;
  const kostnad = kostpris * Math.max(1, Number(l.qty) || 1);
  const bidrag = salgEksMva - kostnad;
  return { bidrag, dg: salgEksMva > 0 ? (bidrag / salgEksMva) * 100 : null };
}

// Sum for linjene som har kostpris, og hvor mange (ikke-garanti) som mangler den.
export function delerBidrag(partLines = []) {
  let salgEksMva = 0;
  let bidrag = 0;
  let medKostpris = 0;
  let manglerKostpris = 0;
  for (const l of partLines) {
    if (l.isWarranty) continue;
    const b = delelinjeBidrag(l);
    if (!b) {
      manglerKostpris += 1;
      continue;
    }
    medKostpris += 1;
    bidrag += b.bidrag;
    salgEksMva += linjesum(l) / MVA;
  }
  return {
    bidrag,
    dg: salgEksMva > 0 ? (bidrag / salgEksMva) * 100 : null,
    medKostpris,
    manglerKostpris,
  };
}
